import React from "react";
import { NavLink } from "react-router-dom";
import { Mail, Phone, MapPin, Facebook, Instagram } from "lucide-react";
import logo from "../../assets/Easymarg logo.png";

const Footer = () => {
  const getLinkClass = () =>
    `mb-3 text-sm text-slate-300 hover:text-white transition-colors duration-200`;

  return (
    <footer 
      className="relative text-white overflow-hidden font-outfit"
      style={{ backgroundColor: "#020617" }}
    >

      {/* Subtle Pattern */}
      <div className="absolute inset-0 opacity-[0.04] z-0 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
            backgroundSize: "40px 40px",
          }}
        ></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-4 md:py-12">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 lg:gap-16 mb-12 md:mb-16">
          {/* Brand Section */}
          <div className="col-span-2 lg:col-span-1 space-y-6 md:space-y-8">
            <div className="flex items-center gap-3">
              <div className="bg-white px-3 py-1.5 rounded-lg shadow-md">
                <img src={logo} alt="EasyMarg Logo" className="h-7 object-contain" />
              </div>
              {/* <span className="text-xl font-black tracking-tight">EasyMarg</span> */}
            </div>
            
            <p className="text-slate-200 leading-relaxed text-sm md:text-base font-medium opacity-90 max-w-xs">
              Empowering individuals and businesses with innovative financial management and expert-driven consultation services.
            </p>

            {/* Social Media Icons */}
            <div className="flex gap-4">
              {[
                { icon: Facebook, href: "https://www.facebook.com/profile.php?id=61558828424026" },
                // { icon: Twitter, href: "https://twitter.com" },
                // { icon: Linkedin, href: "https://linkedin.com" },
                { icon: Instagram, href: "https://www.instagram.com/easymarg/" }
              ].map((social, idx) => (
                <a
                  key={idx}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 rounded-xl bg-white/10 hover:bg-white/25 border border-white/10 flex items-center justify-center transition-all duration-300 hover:scale-110 active:scale-95"
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div className="col-span-1 lg:pl-8">
            <h4 className="text-white mb-6 md:mb-8 font-black text-xs uppercase tracking-[0.2em]">Quick Links</h4>
            <div className="flex flex-col">
              <NavLink to="/home" className={getLinkClass}>Home</NavLink>
              <NavLink to="/our-experts" className={getLinkClass}>Find Professionals</NavLink>
              <NavLink to="/profile/my-bookings" className={getLinkClass}>My Bookings</NavLink>
              <NavLink to="/services" className={getLinkClass}>Services</NavLink>
              <NavLink to="/categories" className={getLinkClass}>Categories</NavLink>
            </div>
          </div>

          {/* Company */}
          <div className="col-span-1 lg:pl-4">
            <h4 className="text-white mb-6 md:mb-8 font-black text-xs uppercase tracking-[0.2em]">Our Company</h4>
            <div className="flex flex-col">
              <NavLink to="/about" className={getLinkClass}>About Us</NavLink>
              <NavLink to="/contact" className={getLinkClass}>Contact Us</NavLink>
              <NavLink to="/faq" className={getLinkClass}>FAQ Center</NavLink>
            </div>
          </div>


          {/* Contact Section */}
          <div className="col-span-2 lg:col-span-1">
            <h4 className="text-white mb-6 md:mb-8 font-black text-xs uppercase tracking-[0.2em]">Direct Support</h4>
            <div className="space-y-6">
              <div className="flex items-start gap-4 group">
                <a href="mailto:support@easymarg.com" className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center border border-white/5 flex-shrink-0 hover:bg-blue-500 hover:border-blue-500 transition-all duration-300">
                  <Mail className="w-5 h-5 text-blue-300 group-hover:text-white transition-colors" />
                </a>
                <div>
                  <p className="text-[10px] font-bold text-blue-200/50 uppercase tracking-widest mb-1">Email Us</p>
                  <a href="mailto:support@easymarg.com" className="text-sm font-bold text-white hover:text-blue-300 transition-colors">
                    support@easymarg.com
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <a href="tel:+919930919980" className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center border border-white/5 flex-shrink-0 hover:bg-indigo-500 hover:border-indigo-500 transition-all duration-300">
                  <Phone className="w-5 h-5 text-blue-300 group-hover:text-white transition-colors" />
                </a>
                <div>
                  <p className="text-[10px] font-bold text-blue-200/50 uppercase tracking-widest mb-1">Call Anywhere</p>
                  <a href="tel:+919930919980" className="text-sm font-bold text-white hover:text-indigo-300 transition-colors">
                    +91 99309 19980
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <a 
                  href="https://www.google.com/maps/search/?api=1&query=Bangalore,Karnataka,India" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center border border-white/5 flex-shrink-0 hover:bg-emerald-500 hover:border-emerald-500 transition-all duration-300"
                >
                  <MapPin className="w-5 h-5 text-blue-300 group-hover:text-white transition-colors" />
                </a>
                <div>
                  <p className="text-[10px] font-bold text-blue-200/50 uppercase tracking-widest mb-1">Headquarters</p>
                  <a 
                    href="https://www.google.com/maps/search/?api=1&query=Bangalore,Karnataka,India" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-sm font-bold text-white hover:text-emerald-300 transition-colors"
                  >
                    Bangalore, Karnataka, IN
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-10 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex flex-col md:flex-row items-center gap-3 text-center md:text-left">
              <p className="text-xs text-slate-300 font-medium tracking-wide">
                © {new Date().getFullYear()} <span className="text-white font-black uppercase">GetProtech Solutions Pvt Ltd.</span>
              </p>
              <div className="hidden md:block w-1.5 h-1.5 bg-white/20 rounded-full"></div>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                All Rights Reserved
              </p>
            </div>
            
            <div className="flex gap-8">
              <NavLink to="/privacy" className="text-xs font-bold text-slate-300 hover:text-white uppercase tracking-[0.15em] transition-colors">
                Privacy
              </NavLink>
              <NavLink to="/terms" className="text-xs font-bold text-slate-300 hover:text-white uppercase tracking-[0.15em] transition-colors">
                Terms
              </NavLink>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
